import axios from '/webpack-assets/vue/config/axios-config';

const TOKEN_URL = '/chat-api/get-token';
const REFRESH_BEFORE = 5;
const STORAGE_KEY = 'chat_token_cache';
const UNAUTHORIZED_STATUSES = [401, 403];

let token = null;
let exp = 0;
let refreshTimer = null;
let pendingPromise = null;

function now() {
  return Math.floor(Date.now() / 1000);
}

function getUserId() {
  return window?.common?.user?.id || null;
}

function loadFromCache() {
  try {
    const cached = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!cached) return;

    const currentUserId = getUserId();

    if (cached.userId !== currentUserId) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    if (cached.exp > now()) {
      token = cached.token;
      exp = cached.exp;
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
}

function saveToCache() {
  const userId = getUserId();

  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ token, exp, userId })
  );
}

function scheduleRefresh() {
  if (!exp) return;

  const delay = Math.max((exp - REFRESH_BEFORE - now()) * 1000, 0);

  clearTimeout(refreshTimer);

  refreshTimer = setTimeout(() => {
    fetchToken().catch(() => {});
  }, delay);
}

async function fetchToken() {
  if (pendingPromise) return pendingPromise;

  pendingPromise = axios.get(TOKEN_URL)
    .then(res => {
      if (res.data?.status !== 'success') {
        throw new Error('Token request failed');
      }

      token = res.data.data.token;
      exp = res.data.data.exp;

      saveToCache();
      scheduleRefresh();

      return token;
    })
    .finally(() => {
      pendingPromise = null;
    });

  return pendingPromise;
}

export async function getChatToken() {

  if (!token) {
    loadFromCache();
  }

  if (token && exp - now() > REFRESH_BEFORE) {
    scheduleRefresh();
    return token;
  }

  return fetchToken();
}

function requestWithToken(url, options, bearer) {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${bearer}`,
    },
  });
}

async function tokenAfterReject(staleToken) {
  if (staleToken && staleToken !== token) {
    return getChatToken();
  }

  clearChatToken();

  return getChatToken();
}

export async function chatFetch(url, options = {}) {
  const currentToken = await getChatToken();
  const response = await requestWithToken(url, options, currentToken);

  if (!UNAUTHORIZED_STATUSES.includes(response.status)) {
    return response;
  }

  return requestWithToken(url, options, await tokenAfterReject(currentToken));
}

export function clearChatToken() {
  token = null;
  exp = 0;

  localStorage.removeItem(STORAGE_KEY);

  clearTimeout(refreshTimer);
  refreshTimer = null;
}